import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Heart,
  Users,
  BookOpen,
  MessageSquare,
  Building2,
  GraduationCap,
  CheckCircle,
  Send,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SEO, { createFAQSchema } from '../components/SEO';
import { volunteerRoles } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GetInvolved = () => {
  const [volunteerForm, setVolunteerForm] = useState({
    name: '', email: '', phone: '', role: '', availability: '', comments: ''
  });
  const [familyForm, setFamilyForm] = useState({
    parentName: '', scholarName: '', grade: '', school: '', email: '', phone: '', programs: []
  });
  const [partnerForm, setPartnerForm] = useState({
    organization: '', contact: '', email: '', phone: '', partnershipType: '', message: ''
  });
  const [loading, setLoading] = useState({ volunteer: false, family: false, partner: false });

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    setLoading(prev => ({ ...prev, volunteer: true }));
    try {
      const response = await axios.post(`${API}/forms/volunteer`, volunteerForm);
      toast.success(response.data.message);
      setVolunteerForm({ name: '', email: '', phone: '', role: '', availability: '', comments: '' });
    } catch (error) {
      toast.error('Failed to submit form. Please try again.');
      console.error('Volunteer form error:', error);
    } finally {
      setLoading(prev => ({ ...prev, volunteer: false }));
    }
  };

  const handleFamilySubmit = async (e) => {
    e.preventDefault();
    setLoading(prev => ({ ...prev, family: true }));
    try {
      const response = await axios.post(`${API}/forms/family`, familyForm);
      toast.success(response.data.message);
      setFamilyForm({ parentName: '', scholarName: '', grade: '', school: '', email: '', phone: '', programs: [] });
    } catch (error) {
      toast.error('Failed to submit form. Please try again.');
      console.error('Family form error:', error);
    } finally {
      setLoading(prev => ({ ...prev, family: false }));
    }
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    setLoading(prev => ({ ...prev, partner: true }));
    try {
      const response = await axios.post(`${API}/forms/partner`, partnerForm);
      toast.success(response.data.message);
      setPartnerForm({ organization: '', contact: '', email: '', phone: '', partnershipType: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit form. Please try again.');
      console.error('Partner form error:', error);
    } finally {
      setLoading(prev => ({ ...prev, partner: false }));
    }
  };

  const programOptions = [
    'Tutoring', 'Mentoring', 'Music Program', 'Podcasting', 'Cosmetology', 
    'Apparel Design', 'Videography', 'E-Gaming & Coding', 'Summer Camp', 'After-School Program'
  ];

  // FAQ Schema for volunteer/involvement questions
  const faqSchema = createFAQSchema([
    {
      question: "How can I volunteer with Mentors in Motion?",
      answer: "You can volunteer by filling out our volunteer form on this page. We offer various roles including tutoring, mentoring, event support, and administrative assistance. All volunteers undergo a background check and training."
    },
    {
      question: "How do I enroll my child in Mentors in Motion programs?",
      answer: "Parents can enroll their children by completing the Family Interest Form on this page. Our programs serve youth in the St. Louis area and include tutoring, mentoring, and creative learning opportunities."
    },
    {
      question: "How can my organization partner with Mentors in Motion?",
      answer: "We welcome partnerships with schools, businesses, and community organizations. Fill out our Partnership Inquiry form to discuss sponsorship, program collaboration, or resource sharing opportunities."
    },
    {
      question: "What age groups does Mentors in Motion serve?",
      answer: "Mentors in Motion serves youth primarily between ages 10-18 in the St. Louis metropolitan area through our various tutoring, mentoring, and creative learning programs."
    }
  ]);

  return (
    <div>
      <SEO 
        title="Get Involved - Volunteer & Enroll"
        description="Volunteer as a mentor or tutor in St. Louis. Enroll your child in youth programs or partner with Mentors in Motion."
        keywords="volunteer youth mentor St. Louis, tutoring volunteer Missouri, nonprofit volunteer opportunities, youth program enrollment, community partnership nonprofit"
        canonicalUrl="/get-involved"
        structuredData={faqSchema}
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(227,27,35,0.3),transparent_50%)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              Get Involved
            </h1>
            <p className="text-base text-gray-300 leading-relaxed">
              It truly takes a village to raise a child. By getting involved with Mentors in Motion as a mentor, tutor, volunteer, parent partner, or organizational ally you help create the support system our scholars need to thrive. Your time, expertise, and resources translate into better reading and math skills, stronger confidence, and real-world opportunities for young people who are often overlooked. In the process, you not only change students' lives you strengthen families, schools, and the future of St. Louis.
            </p>
          </div>
        </div>
      </section>

      {/* Volunteer Opportunities Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">
              Make a Difference
            </h2>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Ways to Get Involved
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Your time, talent, and support can transform a young scholar's life.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {volunteerRoles.map((role, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    {index === 0 ? <Users className="w-8 h-8 text-red-600" /> :
                     index === 1 ? <BookOpen className="w-8 h-8 text-red-600" /> :
                     <MessageSquare className="w-8 h-8 text-red-600" />}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{role.title}</h3>
                  <p className="text-gray-600">{role.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="volunteer" className="w-full">
            <TabsList className="grid grid-cols-3 gap-4 mb-12 bg-transparent h-auto">
              <TabsTrigger
                value="volunteer"
                className="flex flex-col items-center gap-2 p-4 rounded-xl data-[state=active]:bg-red-600 data-[state=active]:text-white bg-white shadow-sm hover:bg-gray-100 transition-colors h-auto"
              >
                <Users className="w-6 h-6" />
                <span>Volunteer</span>
              </TabsTrigger>
              <TabsTrigger
                value="family"
                className="flex flex-col items-center gap-2 p-4 rounded-xl data-[state=active]:bg-red-600 data-[state=active]:text-white bg-white shadow-sm hover:bg-gray-100 transition-colors h-auto"
              >
                <GraduationCap className="w-6 h-6" />
                <span>Family Enrollment</span>
              </TabsTrigger>
              <TabsTrigger
                value="partner"
                className="flex flex-col items-center gap-2 p-4 rounded-xl data-[state=active]:bg-red-600 data-[state=active]:text-white bg-white shadow-sm hover:bg-gray-100 transition-colors h-auto"
              >
                <Building2 className="w-6 h-6" />
                <span>Partner With Us</span>
              </TabsTrigger>
            </TabsList>

            {/* Volunteer Form */}
            <TabsContent value="volunteer">
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-gray-50 border-b">
                  <CardTitle className="flex items-center gap-3">
                    <Users className="w-6 h-6 text-red-600" />
                    Volunteer Sign-Up
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8">
                  <form onSubmit={handleVolunteerSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="vol-name">Full Name *</Label>
                        <Input
                          id="vol-name"
                          value={volunteerForm.name}
                          onChange={(e) => setVolunteerForm({...volunteerForm, name: e.target.value})}
                          required
                          placeholder="Your full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vol-email">Email *</Label>
                        <Input
                          id="vol-email"
                          type="email"
                          value={volunteerForm.email}
                          onChange={(e) => setVolunteerForm({...volunteerForm, email: e.target.value})}
                          required
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="vol-phone">Phone</Label>
                        <Input
                          id="vol-phone"
                          type="tel"
                          value={volunteerForm.phone}
                          onChange={(e) => setVolunteerForm({...volunteerForm, phone: e.target.value})}
                          placeholder="(314) 555-0123"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vol-role">Role Interested In *</Label>
                        <Select
                          value={volunteerForm.role}
                          onValueChange={(value) => setVolunteerForm({...volunteerForm, role: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mentor">Mentor</SelectItem>
                            <SelectItem value="tutor">Tutor</SelectItem>
                            <SelectItem value="guest-speaker">Guest Speaker</SelectItem>
                            <SelectItem value="general">General Volunteer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vol-availability">Availability</Label>
                      <Input
                        id="vol-availability"
                        value={volunteerForm.availability}
                        onChange={(e) => setVolunteerForm({...volunteerForm, availability: e.target.value})}
                        placeholder="e.g., Weekday evenings, Saturday mornings"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vol-comments">Additional Comments</Label>
                      <Textarea
                        id="vol-comments"
                        value={volunteerForm.comments}
                        onChange={(e) => setVolunteerForm({...volunteerForm, comments: e.target.value})}
                        rows={4}
                        placeholder="Tell us about yourself and why you want to volunteer..."
                      />
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600">
                      <CheckCircle className="w-5 h-5 text-green-500 inline mr-2" />
                      Background checks and training will be provided for all volunteers.
                    </div>

                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white" disabled={loading.volunteer}>
                      {loading.volunteer ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" /> Submit Application</>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Family Form */}
            <TabsContent value="family">
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-gray-50 border-b">
                  <CardTitle className="flex items-center gap-3">
                    <GraduationCap className="w-6 h-6 text-red-600" />
                    Family Interest Form
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8">
                  <form onSubmit={handleFamilySubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="fam-parent">Parent/Guardian Name *</Label>
                        <Input
                          id="fam-parent"
                          value={familyForm.parentName}
                          onChange={(e) => setFamilyForm({...familyForm, parentName: e.target.value})}
                          required
                          placeholder="Parent/Guardian name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="fam-scholar">Scholar Name *</Label>
                        <Input
                          id="fam-scholar"
                          value={familyForm.scholarName}
                          onChange={(e) => setFamilyForm({...familyForm, scholarName: e.target.value})}
                          required
                          placeholder="Student's name"
                        />
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="fam-grade">Grade Level *</Label>
                        <Select
                          value={familyForm.grade}
                          onValueChange={(value) => setFamilyForm({...familyForm, grade: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select grade" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pre-k">Pre-K</SelectItem>
                            <SelectItem value="k">Kindergarten</SelectItem>
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(g => (
                              <SelectItem key={g} value={g.toString()}>{g}th Grade</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="fam-school">Current School</Label>
                        <Input
                          id="fam-school"
                          value={familyForm.school}
                          onChange={(e) => setFamilyForm({...familyForm, school: e.target.value})}
                          placeholder="School name"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="fam-email">Email *</Label>
                        <Input
                          id="fam-email"
                          type="email"
                          value={familyForm.email}
                          onChange={(e) => setFamilyForm({...familyForm, email: e.target.value})}
                          required
                          placeholder="your@email.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="fam-phone">Phone *</Label>
                        <Input
                          id="fam-phone"
                          type="tel"
                          value={familyForm.phone}
                          onChange={(e) => setFamilyForm({...familyForm, phone: e.target.value})}
                          required
                          placeholder="(314) 555-0123"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label>Programs of Interest</Label>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {programOptions.map((program) => (
                          <div key={program} className="flex items-center space-x-2">
                            <Checkbox
                              id={`prog-${program}`}
                              checked={familyForm.programs.includes(program)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setFamilyForm({...familyForm, programs: [...familyForm.programs, program]});
                                } else {
                                  setFamilyForm({...familyForm, programs: familyForm.programs.filter(p => p !== program)});
                                }
                              }}
                            />
                            <label htmlFor={`prog-${program}`} className="text-sm text-gray-700 cursor-pointer">
                              {program}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg text-sm text-green-800">
                      <CheckCircle className="w-5 h-5 text-green-600 inline mr-2" />
                      All programs are free or minimal cost to families!
                    </div>

                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white" disabled={loading.family}>
                      {loading.family ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" /> Submit Interest Form</>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Partner Form */}
            <TabsContent value="partner">
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-gray-50 border-b">
                  <CardTitle className="flex items-center gap-3">
                    <Building2 className="w-6 h-6 text-red-600" />
                    Partnership Inquiry
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8">
                  <form onSubmit={handlePartnerSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="part-org">Organization Name *</Label>
                        <Input
                          id="part-org"
                          value={partnerForm.organization}
                          onChange={(e) => setPartnerForm({...partnerForm, organization: e.target.value})}
                          required
                          placeholder="Your organization"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="part-contact">Contact Name *</Label>
                        <Input
                          id="part-contact"
                          value={partnerForm.contact}
                          onChange={(e) => setPartnerForm({...partnerForm, contact: e.target.value})}
                          required
                          placeholder="Your name"
                        />
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="part-email">Email *</Label>
                        <Input
                          id="part-email"
                          type="email"
                          value={partnerForm.email}
                          onChange={(e) => setPartnerForm({...partnerForm, email: e.target.value})}
                          required
                          placeholder="contact@organization.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="part-phone">Phone</Label>
                        <Input
                          id="part-phone"
                          type="tel"
                          value={partnerForm.phone}
                          onChange={(e) => setPartnerForm({...partnerForm, phone: e.target.value})}
                          placeholder="(314) 555-0123"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="part-type">Partnership Type *</Label>
                      <Select
                        value={partnerForm.partnershipType}
                        onValueChange={(value) => setPartnerForm({...partnerForm, partnershipType: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select partnership type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="corporate">Corporate Sponsorship</SelectItem>
                          <SelectItem value="volunteer">Employee Volunteer Program</SelectItem>
                          <SelectItem value="school">School/University Partnership</SelectItem>
                          <SelectItem value="foundation">Foundation/Major Donor</SelectItem>
                          <SelectItem value="nonprofit">Nonprofit Collaboration</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="part-message">Tell Us About Your Interest *</Label>
                      <Textarea
                        id="part-message"
                        value={partnerForm.message}
                        onChange={(e) => setPartnerForm({...partnerForm, message: e.target.value})}
                        required
                        rows={4}
                        placeholder="How would you like to partner with Mentors in Motion?"
                      />
                    </div>

                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white" disabled={loading.partner}>
                      {loading.partner ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" /> Submit Inquiry</>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Join Us in Developing Scholars That Build Thriving Communities
          </h2>
          <p className="text-xl text-red-100 mb-10">
            Your involvement, whether as a volunteer, family, or partner, is as vital as any donation.
          </p>
          <Link to="/donate">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 font-semibold px-8">
              <Heart className="w-5 h-5 mr-2" />
              Donate Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GetInvolved;
